"use client";

import Image from "next/image";

import { GLOBALS } from "config";
import Select from "./Select";
import { StripeCustomerExtended } from "app/api/checkout/cardInfo/route";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { CheckedEpisodeDetail, useStaticStore, useToast, useUserPoints } from "app/hooks";
import { PAYMENT_FAILED_ERROR, STORE_KEYS } from "app/constants";
import { CreditCardPaymentResponseModel, ProductModel, RentalRequestModel } from "@api/models";
import { ResponseError } from "@api/runtime";
import ChargePaymentMethods from "app/(app)/(common)/(common)/components/ChargePaymentMethods";
import StripeRegister from "./StripeRegister";
import { paymentTrigger } from "./Payment";
import { usePathname } from "next/navigation";
import { CheckoutRequest } from "app/api/checkout/route";
import { deleteCampaignPaymentCookie } from "@lib/analyticLog";
import { FreeTicketIcon } from "./Icons";
import { RentalOrBuy } from "./RentalOrBuy";
import { calcNewAmountAndTicketWithCheckout } from "@lib/utils";

type NewTotalPriceAndTicket = {
  productId: string;
  newTotalPrice: number;
  newTotalBonusTicket: number;
};

//カード登録済みの場合の画面

export function CoinAndRentalContent({
  cardInfo,
  checkedEpisodes,
  selectedPointId,
  isSingle,
  name,
  seriesId,
  seriesIndexId,
  totalPrice,
  totalBonusTicket,
  points,
  closeFunction,
  setSelectedPoint,
  setIsCharging,
}: {
  cardInfo?: StripeCustomerExtended;
  selectedPointId: string | number;
  checkedEpisodes: CheckedEpisodeDetail[];
  isSingle: boolean;
  name: string;
  seriesId: string | null;
  seriesIndexId: number | undefined;
  totalPrice: number;
  totalBonusTicket: number;
  points: ProductModel[];
  closeFunction: () => void;
  setSelectedPoint: Dispatch<SetStateAction<string | number>>;
  setIsCharging: Dispatch<SetStateAction<boolean>>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  //toast
  const { pushToast } = useToast();

  // ダイアログの開閉
  const { mutate: setIsCoinAndRentalDialogOpen } = useStaticStore(STORE_KEYS.coinAndRental);

  // 保有ポイント
  const { isLoading: isLoadingUserPoints, userPoints } = useUserPoints();

  const [pointOptions, setPointOptions] = useState<{ value: string; label: string }[]>([]);
  const pathname = usePathname();

  const selectedEpisodes = checkedEpisodes.filter((ep) => ep !== null);
  const [newTotalPriceAndTicket, setNewTotalPriceAndTicket] = useState<NewTotalPriceAndTicket[]>(
    [],
  );

  useEffect(() => {
    if (points) {
      // ポイント+チケット購入を考慮してチケット使用枚数の再計算を行う
      const tmpNewPriceAndTicketArray: NewTotalPriceAndTicket[] = [];
      const filteredPoints = points.filter((item, index) => {
        const { newTotalPrice, newTotalBonusTicket } = calcNewAmountAndTicketWithCheckout(
          selectedEpisodes,
          userPoints,
          item,
        );
        tmpNewPriceAndTicketArray.push({
          productId: item.id,
          newTotalPrice: newTotalPrice,
          newTotalBonusTicket: newTotalBonusTicket,
        });
        const coinAfterCharge = (userPoints?.paidCoin || 0) + (item.points?.value || 0);
        return coinAfterCharge >= newTotalPrice;
      });
      if (filteredPoints.length === 0) {
        // 最大コイン購入でも足りない場合
        const maxPoint = points.reduce((acc, curr) => {
          if (acc.price > curr.price) return acc;
          return curr;
        });
        setPointOptions([{ value: maxPoint.id, label: maxPoint.points?.value.toString() || "" }]);
        setSelectedPoint(maxPoint.id);
      } else {
        setPointOptions(
          filteredPoints.map((item) => {
            return { value: item.id, label: item.points?.value.toString() || "" };
          }),
        );
        setSelectedPoint(filteredPoints[0].id);
        setNewTotalPriceAndTicket(tmpNewPriceAndTicketArray);
      }
    }
  }, [totalPrice, points]);

  const handleClickCoinAndRental = async () => {
    //カード情報がある時
    if (cardInfo?.cardToken) {
      setIsLoading(true);
      if (!paymentTrigger.triggerSubmit) {
        return;
      }
      const onErrorHandle = (message?: string) => {
        if (message) {
          pushToast(message, { type: "error" });
        } else {
          pushToast("エラーが発生しました。時間をおいて再度お試しください。", {
            type: "error",
          });
        }
        setTimeout(() => {
          setIsLoading(false);
          //setIsCoinAndRentalDialogOpen(false);
          //closeFunction();
        }, 1000);
      };
      paymentTrigger
        .triggerSubmit()
        .then(async (res) => {
          const selected = points.filter((p) => p.id === selectedPointId)[0];
          const selectedTotalPoint =
            (selected.points?.value || 0) + (selected.points?.bonusValue || 0);
          const selectedNewTotals = newTotalPriceAndTicket.find(
            (item) => item.productId === selectedPointId,
          );
          await fetch("/api/checkout", {
            method: "POST",
            body: JSON.stringify({
              pointTypeId: Number(selectedPointId),
              cvcToken: res?.token,
              message: `${selectedTotalPoint} コインお得!`,
              redirectUrl: GLOBALS.HOME_URL + pathname,
              articleIds: selectedEpisodes.map((ep) => ep.articleHash),
              articleIndexIds: selectedEpisodes.map((ep) => ep.articleIndexId),
              ...(seriesId && { seriesId: seriesId }),
              ...(seriesIndexId && { seriesIndexId: seriesIndexId }),
              amount: selectedNewTotals?.newTotalPrice,
              ticketCount: selectedNewTotals?.newTotalBonusTicket,
            } as CheckoutRequest),
          })
            .then(async (res) => {
              if (res.status !== 200) {
                // 400エラーの場合はBEからエラー理由のメッセージが返ってくるのでそれをトーストに表示
                if (res.status === 400 && res.body) {
                  const json = await res.json();
                  if (json.message) {
                    throw new Error(json.message, {
                      cause: PAYMENT_FAILED_ERROR,
                    });
                  }
                }
                throw new Error();
              }
              const data = (await res.json()) as CreditCardPaymentResponseModel;
              if (data["_3dSecureRedirectUrl"]) {
                // 3DSリダイレクト
                window.location.href = data["_3dSecureRedirectUrl"];
              } else {
                // 3DSリダイレクトがなければチャージ処理まで行われているため状態取得は必要なし
                // TODO: 表示するメッセージ内容検討(ボーナス含めるか等)
                pushToast(`${selected.points?.value}コインを購入しました。`);
                // そのままレンタル処理
                fetch("/api/checkout/rental", {
                  method: "POST",
                  body: JSON.stringify({
                    articleIds: selectedEpisodes.map((ep) => ep.articleHash),
                    ...(!isSingle && { seriesId: seriesId }),
                    amount: newTotalPriceAndTicket[Number(selectedPointId)].newTotalPrice,
                    ticketCount:
                      newTotalPriceAndTicket[Number(selectedPointId)].newTotalBonusTicket,
                  } as RentalRequestModel),
                })
                  .then(async (res) => {
                    if (res.status !== 200) {
                      throw new ResponseError(res);
                    }
                    pushToast(`エピソードを${GLOBALS.IS_RENTAL ? "レンタル" : "購入"}しました。`);
                    checkedEpisodes.map((ep) => {
                      deleteCampaignPaymentCookie("article", ep?.articleIndexId);
                    });
                    deleteCampaignPaymentCookie("series", seriesIndexId);
                    setIsCoinAndRentalDialogOpen(false);
                    setTimeout(() => {
                      closeFunction();
                      setIsLoading(false);
                      window.location.reload();
                    }, 1000);
                  })
                  .catch((e) => {
                    onErrorHandle();
                  });
              }
            })
            .catch((e) => {
              if (e instanceof Error && e.cause === PAYMENT_FAILED_ERROR) {
                onErrorHandle(e.message);
              } else {
                onErrorHandle();
              }
            });
        })
        .catch(() => {
          // カード登録エラーなのでエラー表示はここでは行わない
          closeFunction();
          setIsLoading(false);
        });
    } else {
      //カード登録されてないのでカード登録モードに移行させる
      setIsCharging(true);
    }
  };
  return (
    <>
      {" "}
      <div className="dialog-pay-h">
        <div className="dialog-pay-h-main">
          <span className="dialog-pay-h-title">{name}</span>
          <span className="dialog-pay-h-price">
            <Image
              src="/images/icons/coin.svg"
              width={18}
              height={18}
              alt="コイン"
              className="dialog-pay-icon"
            />
            {totalPrice}
            {totalBonusTicket > 0 && (
              <>
                <FreeTicketIcon size={22} className="dialog-pay-icon mode-h-ticket" />
                {totalBonusTicket}枚
              </>
            )}
          </span>
        </div>
        {GLOBALS.IS_RENTAL && (
          <p className="dialog-pay-h-sub">
            レンタル期間：<span className="dialog-pay-h-period">{GLOBALS.RENTAL_PERIOD}</span>
          </p>
        )}
      </div>
      <div className="dialog-pay-main">
        <div className="dialog-pay-line">
          <h3 className="dialog-pay-line-h">保有コイン</h3>
          <div className="dialog-pay-line-item">
            <Image
              src="/images/icons/coin.svg"
              width={18}
              height={18}
              alt="コイン"
              className="dialog-pay-icon"
            />
            {userPoints?.paidCoin || 0}
          </div>
        </div>
        {totalBonusTicket > 0 && (
          <div className="dialog-pay-line">
            <h3 className="dialog-pay-line-h">保有チケット</h3>
            <div className="dialog-pay-line-item">
              <FreeTicketIcon size={22} className="dialog-pay-icon" />
              {userPoints?.bonusValue || 0}枚
            </div>
          </div>
        )}
        <div className="dialog-pay-line">
          <h3 className="dialog-pay-line-h mode-lack">不足コイン</h3>
          <div className="dialog-pay-line-item mode-lack">
            <Image
              src="/images/icons/coin.svg"
              width={18}
              height={18}
              alt="コイン"
              className="dialog-pay-icon"
            />
            {totalPrice - (userPoints?.paidCoin || 0)}
          </div>
        </div>
        <div className="dialog-pay-line">
          <h3 className="dialog-pay-line-h">チャージコイン</h3>
          <div className="dialog-pay-line-item mode-chargecoin">
            <Image
              src="/images/icons/coin.svg"
              width={18}
              height={18}
              alt="コイン"
              className="dialog-pay-icon"
            />
            <Select
              values={pointOptions}
              value={selectedPointId}
              onValueChange={(value) => setSelectedPoint(value as number)}
            />
          </div>
        </div>
        {points.find((item) => item.id === selectedPointId)?.points?.bonusValue !== 0 && (
          <div className="dialog-pay-line mode-bonus">
            <span className="dialog-pay-bonus-label">+すぐ無料チケット</span>
            <FreeTicketIcon size={22} className="dialog-pay-icon" />
            {points.find((item) => item.id === selectedPointId)?.points?.bonusValue}枚
          </div>
        )}
        <ChargePaymentMethods isDialog={true} />
        {cardInfo?.cardToken && <StripeRegister inputOnly cvcOnly />}
      </div>
      <div className="dialog-btns">
        <button
          type="button"
          className={"dialog-btn mode-pay" + (isLoading ? " mode-loading" : "")}
          onClick={() => handleClickCoinAndRental()}
          disabled={isLoading}
        >
          <RentalOrBuy />
          {`する ¥${points.find((item) => item.id === selectedPointId)?.price}`}
        </button>

        <button
          type="button"
          className="dialog-btn mode-moderate"
          onClick={closeFunction}
          disabled={isLoading}
        >
          キャンセルする
        </button>
      </div>
    </>
  );
}
